/**
 * Configuracion de entorno.
 *
 * Todas las variables se leen desde aca; ningun componente toca
 * `import.meta.env` directamente.
 */

interface EnvironmentConfig {
  app: {
    name: string;
    version: string;
    env: string;
  };
  api: {
    baseURL: string;
    timeout: number;
  };
  auth: {
    /** Issuer del User Pool: https://cognito-idp.<region>.amazonaws.com/<poolId> */
    authority: string;
    /** Dominio de la pantalla de login: https://login.empresa.com */
    loginDomain: string;
    clientId: string;
    redirectUri: string;
    postLogoutRedirectUri: string;
    scope: string;
    /** Ruta de cierre de sesion del proveedor (Cognito usa `/logout`). */
    logoutPath: string;
    /** Pagina que atiende el iframe de renovacion silenciosa. */
    silentRedirectUri: string;
  };
}

function getEnvVariable(key: string, defaultValue?: string): string {
  const value = import.meta.env[key];
  if (value === undefined && defaultValue === undefined) {
    throw new Error(`Environment variable ${key} is required but not defined`);
  }
  return value ?? defaultValue ?? '';
}

export const env: EnvironmentConfig = {
  app: {
    name: getEnvVariable('VITE_APP_NAME', 'sc-sso-web'),
    version: getEnvVariable('VITE_APP_VERSION', '0.1.0'),
    env: getEnvVariable('VITE_ENV', 'development'),
  },
  api: {
    baseURL: getEnvVariable('VITE_API_BASE_URL', 'http://localhost:3001/api/v1'),
    timeout: parseInt(getEnvVariable('VITE_API_TIMEOUT', '30000'), 10),
  },
  auth: {
    authority: getEnvVariable('VITE_AUTH_AUTHORITY'),
    loginDomain: getEnvVariable('VITE_AUTH_LOGIN_DOMAIN'),
    clientId: getEnvVariable('VITE_AUTH_CLIENT_ID'),
    redirectUri: getEnvVariable(
      'VITE_AUTH_REDIRECT_URI',
      `${window.location.origin}/auth/callback`
    ),
    postLogoutRedirectUri: getEnvVariable(
      'VITE_AUTH_POST_LOGOUT_REDIRECT_URI',
      window.location.origin
    ),
    scope: getEnvVariable('VITE_AUTH_SCOPE', 'openid email profile'),
    logoutPath: getEnvVariable('VITE_AUTH_LOGOUT_PATH', '/logout'),
    silentRedirectUri: getEnvVariable(
      'VITE_AUTH_SILENT_REDIRECT_URI',
      `${window.location.origin}/silent-renew.html`
    ),
  },
};

export const isDevelopment = (): boolean => env.app.env === 'development';

export const isProduction = (): boolean => env.app.env === 'production';
